/*
PURPOSE: Container for dancefloor feed (gestures and music shares) and creation interface

USE CASES:
- Display gestures received by a user or artist
- Display music (tracks and albums) shared by the user
- Display music shared TO this user's dancefloor by others
- Allow contributors to send gestures to the target
- Show empty state when no content exists

DOMAIN BOUNDARIES:
- Handles dancefloor feed display and creation panel coordination
- Uses getDancefloorFeed (cached) for all dancefloor content
- Should NOT handle individual item rendering (delegates to ContentFeedCard)
- Manages loading and empty states

ASSUMPTIONS:
- Target entity (user/artist) provided via props
- Gestures stored with contentType='gesture'
- Music shares stored with entityType='musicTrack' or 'musicRelease'
- Content shared TO user has targetUser in relatedEntities
- Contributors can create gestures, anyone can view them
*/

import React, { useState, useEffect } from 'react';
import { getDancefloorFeed } from '../../api/api';
import { useIsContributor } from '../../hooks/useAccessControl';
import { useAuth } from '../../hooks/useAuth';
import ContentFeed from '../ContentFeed/ContentFeed';
import GestureCreationPanel from '../GestureCreationPanel/GestureCreationPanel';
import ShareMusicPanel from '../ShareMusicPanel/ShareMusicPanel';
import { ContentItem, OriginLocation } from 'shared/types/contentTypes';

// TODO: This will come from user settings once upload workflow is implemented
import dancefloorSample from '../../assets/images/dancefloor-sample.jpg';

// Feature flag: set to true to enable background image
const ENABLE_DANCEFLOOR_IMAGE = false;

interface GesturesTabProps {
  targetUserId: string;
  targetDisplayName: string;
  targetEntityType: 'user' | 'artist' | 'community';
  originLocation?: OriginLocation;
}

const GesturesTab: React.FC<GesturesTabProps> = ({
  targetUserId,
  targetDisplayName,
  targetEntityType,
  originLocation
}) => {
  const [dancefloorItems, setDancefloorItems] = useState<ContentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isShareMusicOpen, setIsShareMusicOpen] = useState(false);
  const isContributor = useIsContributor();
  const { currentUser2: currentUser } = useAuth();

  const fetchDancefloorContent = React.useCallback(async () => {
    try {
      setIsLoading(true);

      // Use cached dancefloor feed API - single call replaces 3 parallel calls
      const response = await getDancefloorFeed(targetEntityType, targetUserId, 100);

      setDancefloorItems(response.items);
    } catch (error) {
      console.error('Error fetching dancefloor content:', error);
      setDancefloorItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [targetEntityType, targetUserId]);

  useEffect(() => {
    fetchDancefloorContent();
  }, [fetchDancefloorContent]);

  const handleGestureCreated = (newGesture: ContentItem) => {
    // Optimistically add to local state at the top (newest first)
    setDancefloorItems(prev => [newGesture, ...prev]);
  };

  const handleMusicShared = () => {
    // Refresh the dancefloor content to show the new share
    fetchDancefloorContent();
    setIsShareMusicOpen(false);
  };

  // Check if viewing own page
  const isOwnPage = currentUser?.userId === targetUserId;

  return (
    <div className={`relative ${ENABLE_DANCEFLOOR_IMAGE ? '-mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8' : ''}`} style={ENABLE_DANCEFLOOR_IMAGE ? { clipPath: 'inset(0)' } : undefined}>
      {/* Fixed Background Image - stays in viewport while scrolling, clipped to this container */}
      {ENABLE_DANCEFLOOR_IMAGE && (
        <div className="fixed inset-0 pointer-events-none">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${dancefloorSample})` }}
          />
          {/* Dark overlay for atmosphere */}
          <div className="absolute inset-0 bg-black/30" />
          {/* Gradient fade - visible at top, fades to page background at bottom */}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-50 via-gray-50/60 via-30% to-transparent" />
        </div>
      )}

      {/* Content - scrolls over the background */}
      <div className={`relative ${ENABLE_DANCEFLOOR_IMAGE ? 'z-10' : ''} max-w-4xl mx-auto pt-4 pb-6`}>
        {/* Gesture Creation Panel - Only for contributors, not on own page */}
        {isContributor && !isOwnPage && (
          <GestureCreationPanel
            targetUserId={targetUserId}
            targetDisplayName={targetDisplayName}
            targetEntityType={targetEntityType}
            onGestureCreated={handleGestureCreated}
            originLocation={originLocation}
            onShareMusicClick={() => setIsShareMusicOpen(true)}
          />
        )}

        {/* Share Music Panel - only for user profiles */}
        {targetEntityType === 'user' && (
          <ShareMusicPanel
            isOpen={isShareMusicOpen}
            onClose={() => setIsShareMusicOpen(false)}
            onContentCreated={handleMusicShared}
            headerText={`Share music with ${targetDisplayName}`}
            explainerText="Share a track or album you think they&apos;d enjoy"
            defaultContentType="discussion"
            targetUser={{ userId: targetUserId, displayName: targetDisplayName }}
            originLocation={originLocation}
          />
        )}

        {/* Dancefloor Feed - Gestures and Music Shares */}
        <ContentFeed
          items={dancefloorItems}
          isLoading={isLoading}
          emptyMessage={`Nothing here yet. ${isContributor && !isOwnPage ? `Be the first to reach out to ${targetDisplayName}!` : `${isOwnPage ? 'You haven' : targetDisplayName + ' hasn'}'t shared anything yet.`}`}
          currentLocation={originLocation}
        />
      </div>
    </div>
  );
};

export default GesturesTab;
